import { ampli } from '@/ampli';
import Decimal from 'decimal.js-light';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMedia } from 'react-use';
import { toast } from 'sonner';
import { formatUnits } from 'viem';
import { useBalance } from 'wagmi';

import { ApiNoLiquidityAvailableError } from '@/lib/errors/api';
import { JupiterTokenNotTradableError } from '@/lib/errors/jupiter';
import { tokenFormatter } from '@/lib/formatting';
import { isTokenRestrictedInCountry } from '@/lib/geo/restrictedIssuers';

import { MatchaAutoBanner } from '@/components/MatchaAutoBanner';
import { SankeyModal } from '@/components/Modal/SankeyModal';
import { SankeyContext } from '@/components/SankeyDiagram/SankeyProvider';
import { isSankeyRouteAvailable } from '@/components/SankeyDiagram/utils';
import { SlippageAlert } from '@/components/TransactionModule/Alerts/SlippageAlert';
import { SuperstateKYCAlert } from '@/components/TransactionModule/Alerts/SuperstateKYCAlert';
import { AmountInputGroup } from '@/components/TransactionModule/AmountInputGroup';
import { InlineError } from '@/components/TransactionModule/Errors/InlineError';
import { ExchangeRate } from '@/components/TransactionModule/ExchangeRate';
import { getTokenRate } from '@/components/TransactionModule/ExchangeRate/utils';
import { InlineSuggestedTokens } from '@/components/TransactionModule/InlineSuggestedTokens';
import { useOwnedSellSuggestions } from '@/components/TransactionModule/InlineSuggestedTokens/utils';
import { ReceiveRecapLineMarketMode } from '@/components/TransactionModule/MarketMode/ReceiveRecapLine';
import { SwapDetailsAccordionMarketMode } from '@/components/TransactionModule/MarketMode/SwapDetailsAccordion';
import { useEstimateNativeFeeReservePrice } from '@/components/TransactionModule/MarketMode/useEstimateNativeFeeReservePrice';
import {
  calculateInput,
  getTokenAmount,
  getTokenInputValues,
} from '@/components/TransactionModule/MarketMode/utils';
import { SectionErrorBoundary } from '@/components/TransactionModule/SectionErrorBoundary';
import {
  BalanceFraction,
  SellTokenQuickActions,
} from '@/components/TransactionModule/SellTokenQuickActions';
import { TooltipPriceImpact } from '@/components/TransactionModule/SwapDetailsAccordion/Tooltips/PriceImpact';
import { SwitchTokensButton } from '@/components/TransactionModule/SwitchTokensButton';
import { useTokenBalanceValue } from '@/components/TransactionModule/TMTokenBalance';

import { SOL_ADDRESS_SOLANA } from '@/constants/addresses';
import { CHAIN_IDS, NETWORK_NAME_PER_CHAIN_ID, solana } from '@/constants/chain';
import { RESERVE_LAMPORTS_FOR_MAX_SOL_SELL } from '@/constants/solana';

import { useAllowance } from '@/hooks/useAllowance';
import { useFiatDisplay } from '@/hooks/useFiatDisplay';
import { useGeoCheck } from '@/hooks/useGeoCheck';
import { useIntentsAvailability } from '@/hooks/useIntentsAvailability';
import { useIsSmartWallet } from '@/hooks/useIsSmartWallet';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { isAutoSlippageActive } from '@/hooks/useResolvedSlippage';
import { useSplTokenBalance, type SolanaTokenAmount } from '@/hooks/useSplTokenBalance';
import { hasRequiredPriceParams } from '@/hooks/useSwapPrice';
import { useTokenFeeConfig } from '@/hooks/useTokenFeeConfig';

import { autoBannerSelectors, useAppStore } from '@/store/app';
import { liquiditySourcesEventHandlers, useLiquiditySourcesStore } from '@/store/liquiditySources';
import {
  matchaAutoSelectors,
  slippageSelector,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';
import {
  MatchaTooltip,
  MatchaTooltipBottomSheet,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { hasInTokenGasFee } from '@/utils/0x.types';
import { isTokenBlockedByTokenList } from '@/utils/0x/token-blocking';
import type { TokenBase } from '@/utils/0x/token-registry.types';
import { SEARCH_TYPES } from '@/utils/amplitude';
import { classNames } from '@/utils/classnames';
import { PRICE_IMPACT_THRESHOLDS, SLIPPAGE_THRESHOLDS, trackError } from '@/utils/errors';
import { getHasEnoughBalanceForAmountPlusFees } from '@/utils/fees';
import { isChainAddress, isEthereumAddress } from '@/utils/models';
import { areEquivalentTokens, areSameTokens, isTokenAddressNativeAsset } from '@/utils/multichain';
import { isNullAmount } from '@/utils/numbers';
import { calculateZeroExFeeInUsd } from '@/utils/quote-fees';
import { lamportToSol } from '@/utils/solana';
import { commify } from '@/utils/string';
import { getIsApprovalRequired, isWrap } from '@/utils/swap';
import { getConnectedChain } from '@/utils/wallet';

import { useModuleContext } from '../ModuleContext';
import { useTradeEngine } from '../useTradeEngine';
import { useTradeInputs } from '../useTradeInputs';
import { useValidation } from '../useValidation';
import { CheckoutAlerts } from './CheckoutAlerts';
import { TokenBalanceLine } from './TokenBalanceLine';
import { TokenInfoButton } from './TokenInfoButton';
import { TokenSelect } from './TokenSelect';
import { TradeCta } from './TradeCta';

const Divider = () => <div className="relative z-1 min-h-px bg-token-gray-300 h-px w-full" />;

/** Renders the same-chain market form from rewrite-owned inputs, quotes, and validation. */
export function MarketPanel() {
  const {
    variant,
    sellToken,
    buyToken,
    isSellTokenLoading,
    isBuyTokenLoading,
    accountAddress,
    onSelectToken,
    onSelectPair,
    onFlipSelectedTokens,
    autoBannerVisible,
    setAutoBannerVisible,
    setSettingsOpen,
  } = useModuleContext();
  const { quote, tokenUsdPrices } = useTradeEngine();
  const currentPrice =
    quote.source === 'swap' || quote.source === 'intents' ? quote.raw : undefined;
  const isLoadingPrice = quote.status === 'loading';
  const isCurrentSwapGasless = quote.source === 'intents';
  const chainId = sellToken?.chainId ?? buyToken?.chainId ?? CHAIN_IDS.MAINNET;
  const { formatFiat } = useFiatDisplay();
  const { ethereumWallet } = useMatchaWallets();
  const connectedChain = getConnectedChain(ethereumWallet);
  const isMobile = useMedia(breakpoints.mobile, false);
  const { setSankeyModalOpen, isOpen: sankeyModalOpen } = useContext(SankeyContext);
  const { isMatchaAutoOn } = useMarketTradeSettingsStore(matchaAutoSelectors);
  const { slippage, slippageMode } = useMarketTradeSettingsStore(slippageSelector);
  const { getExcludedSources } = useLiquiditySourcesStore(liquiditySourcesEventHandlers);
  const { autoBannerDismissedUntil } = useAppStore(autoBannerSelectors);
  const [exchangeRate, setExchangeRate] = useState<{ sell: Decimal; buy: Decimal }>();
  const {
    sellInput,
    buyInput,
    setInputMode,
    switchInputs,
    updateSellInput,
    updateBuyInput,
    updateInputs,
  } = useTradeInputs();

  useEffect(() => {
    if (!quote.buyAmount || !buyToken || !sellToken || !sellInput.decimal) return;
    const buyAmount = new Decimal(formatUnits(BigInt(quote.buyAmount), buyToken.decimals));
    const sellAmount = new Decimal(sellInput.decimal);
    updateBuyInput({
      decimal: buyAmount.toString(),
      usd: buyAmount.times(tokenUsdPrices.buyTokenUsd).toNumber(),
      formatted: isNullAmount(buyAmount.toNumber())
        ? ''
        : tokenFormatter.format(buyAmount, { fullAmount: true }),
    });
    setExchangeRate(getTokenRate({ buyAmount, sellAmount }));
  }, [
    buyToken,
    quote.buyAmount,
    sellInput.decimal,
    sellToken,
    tokenUsdPrices.buyTokenUsd,
    updateBuyInput,
  ]);

  useEffect(() => {
    if (quote.error) updateBuyInput();
  }, [quote.error, updateBuyInput]);

  const sellAmount = useMemo(
    () => getTokenAmount(sellInput.decimal, sellToken?.decimals),
    [sellInput.decimal, sellToken?.decimals],
  );
  const sellTokenBalance = useTokenBalanceValue({
    accountAddress,
    chainId: sellToken?.chainId,
    tokenAddress: sellToken?.address,
  });
  const { data: splTokenBalance, isSuccess: isSplBalanceLoaded } = useSplTokenBalance({
    accountAddress,
    tokenAddress: sellToken?.address,
  });
  const splBalanceLamports = BigInt(splTokenBalance?.amount ?? 0);
  const isSellTokenBalanceZero =
    chainId === solana.id
      ? splBalanceLamports === 0n
      : sellTokenBalance
        ? sellTokenBalance.value === 0n
        : true;
  const isSellTokenBalanceConfirmedZero =
    chainId === solana.id
      ? isSplBalanceLoaded && splBalanceLamports === 0n
      : sellTokenBalance != null && sellTokenBalance.value === 0n;
  const isInsufficientSolForGas =
    chainId === solana.id &&
    sellToken?.address === SOL_ADDRESS_SOLANA &&
    splBalanceLamports <= RESERVE_LAMPORTS_FOR_MAX_SOL_SELL;

  const { data: tokenFeeConfigData } = useTokenFeeConfig(
    {
      chainIdA: chainId,
      tokenAddressA:
        sellToken && isEthereumAddress(sellToken.address) ? sellToken.address : undefined,
      tokenAddressB: buyToken && isEthereumAddress(buyToken.address) ? buyToken.address : undefined,
      chainIdB: chainId,
    },
    { enabled: Boolean(sellToken && buyToken && isCurrentSwapGasless) },
  );
  const isStablecoinPair = Boolean(tokenFeeConfigData?.isStablecoinPair);

  const excludedSources = getExcludedSources(chainId);
  const estimateNativeFeeReservePrice = useEstimateNativeFeeReservePrice({
    buyToken,
    sellToken,
    chainId,
    slippage,
    excludedSources,
    accountAddress,
    buyTokenAmountUsd: buyInput.usd,
  });

  const canSuggestOwnedTokens = Boolean(
    sellToken && buyToken && accountAddress && isSellTokenBalanceConfirmedZero,
  );
  const ownedSuggestionTokens = useOwnedSellSuggestions({
    chainId,
    accountAddress: canSuggestOwnedTokens ? accountAddress : undefined,
    buyTokenAddress: buyToken?.address,
    sellTokenAddress: sellToken?.address,
  });

  const flipSelectedBuySellTokens = useCallback(() => {
    switchInputs();
    onFlipSelectedTokens();
  }, [onFlipSelectedTokens, switchInputs]);

  const flipToPair = useCallback(
    (nextBuyToken: TokenBase, nextSellToken: TokenBase) => {
      switchInputs();
      onSelectPair(nextBuyToken, nextSellToken);
    },
    [onSelectPair, switchInputs],
  );

  const handleSelectToken = useCallback(
    (selectedToken: TokenBase, side: 'buy' | 'sell') => {
      const isBuyToken = side === 'buy';
      const previousToken = isBuyToken ? buyToken : sellToken;
      const otherToken = isBuyToken ? sellToken : buyToken;

      updateBuyInput();
      if (!isBuyToken) {
        updateSellInput(
          getTokenInputValues(sellInput.decimal, selectedToken.decimals, sellInput.usd),
        );
      }

      if (areSameTokens(selectedToken, otherToken)) {
        flipSelectedBuySellTokens();
      } else if (previousToken && areEquivalentTokens(selectedToken, otherToken)) {
        flipToPair(
          isBuyToken ? selectedToken : previousToken,
          isBuyToken ? previousToken : selectedToken,
        );
      } else {
        onSelectToken(selectedToken, side);
      }
    },
    [
      buyToken,
      flipSelectedBuySellTokens,
      flipToPair,
      onSelectToken,
      sellInput,
      sellToken,
      updateBuyInput,
      updateSellInput,
    ],
  );

  // Referentially stable so `React.memo(SelectToken)` can skip its modal subtree.
  const handleSelectSellToken = useCallback(
    (token: TokenBase) => handleSelectToken(token, 'sell'),
    [handleSelectToken],
  );
  const handleSelectBuyToken = useCallback(
    (token: TokenBase) => handleSelectToken(token, 'buy'),
    [handleSelectToken],
  );

  const updateSellInputFromSolanaBalance = useCallback(
    (fraction: BalanceFraction, balance: SolanaTokenAmount | undefined) => {
      if (!balance?.uiAmountString) return;
      const rawAmount =
        sellToken?.address === SOL_ADDRESS_SOLANA
          ? BigInt(balance.amount) > RESERVE_LAMPORTS_FOR_MAX_SOL_SELL
            ? BigInt(balance.amount) - RESERVE_LAMPORTS_FOR_MAX_SOL_SELL
            : 0n
          : BigInt(balance.amount);
      updateSellInput(
        calculateInput(rawAmount, balance.decimals, tokenUsdPrices.sellTokenUsd, fraction),
      );
    },
    [sellToken?.address, tokenUsdPrices.sellTokenUsd, updateSellInput],
  );

  const handleBalancePercentageClick = useCallback(
    async (fraction: BalanceFraction, shortcutLabel: string) => {
      setInputMode('tokenAmount');
      if (fraction === 0) {
        updateInputs();
        return;
      }
      if (chainId === solana.id) {
        updateSellInputFromSolanaBalance(fraction, splTokenBalance);
        return;
      }
      if (!sellTokenBalance) return;

      const shouldDeductNativeFees =
        isTokenAddressNativeAsset(sellToken?.address, sellToken?.chainId) && !isCurrentSwapGasless;
      let networkFees = 0;
      if (shouldDeductNativeFees) {
        try {
          networkFees = await estimateNativeFeeReservePrice.mutateAsync({
            sellAmount: (sellTokenBalance.value / BigInt(fraction)).toString(),
            fraction,
          });
        } catch (error: unknown) {
          trackError(error instanceof Error ? error : new Error('unknown'), {
            component: 'Market mode 50%/Max shortcuts: ',
            balDenominator: fraction,
            sellTokenAddress: sellToken?.address,
            sellTokenChainId: sellToken?.chainId,
            buyTokenAddress: buyToken?.address,
          });
          toast.error(`Error setting ${shortcutLabel} amount`, {
            id: 'market-balance-shortcut-error',
            className:
              '!bg-special-background-modal !border !border-token-gray-300 !text-token-gray-800 !rounded-xl',
          });
        }
      }

      const balance = new Decimal(formatUnits(sellTokenBalance.value, sellTokenBalance.decimals))
        .div(fraction)
        .toDecimalPlaces(sellTokenBalance.decimals, Decimal.ROUND_DOWN);
      let balanceAfterFees = balance.minus(networkFees);
      if (balanceAfterFees.lte(0)) balanceAfterFees = new Decimal(0);
      const formatted = balanceAfterFees.gt(100)
        ? balanceAfterFees.toDecimalPlaces(2, Decimal.ROUND_DOWN)
        : balanceAfterFees.toDecimalPlaces(6, Decimal.ROUND_DOWN);
      if (balanceAfterFees.lte(0)) {
        updateInputs();
        toast.error(`Not enough ${sellToken?.symbol} to cover network fees`, {
          id: 'market-balance-shortcut-error',
          className:
            '!bg-special-background-modal !border !border-token-gray-300 !text-token-gray-800 !rounded-xl',
        });
        return;
      }
      updateSellInput({
        usd: formatted.times(tokenUsdPrices.sellTokenUsd).toNumber(),
        decimal: balanceAfterFees.toString(),
        formatted: tokenFormatter.format(balanceAfterFees, { mode: 'write' }),
      });
    },
    [
      buyToken?.address,
      chainId,
      estimateNativeFeeReservePrice,
      isCurrentSwapGasless,
      sellToken,
      sellTokenBalance,
      setInputMode,
      splTokenBalance,
      tokenUsdPrices.sellTokenUsd,
      updateInputs,
      updateSellInput,
      updateSellInputFromSolanaBalance,
    ],
  );

  const isBuyTokenBlocked =
    isTokenBlockedByTokenList(buyToken) ||
    Boolean(
      quote.error instanceof JupiterTokenNotTradableError &&
        buyToken &&
        quote.error.message.includes(buyToken.address),
    );
  const isSellTokenBlocked =
    isTokenBlockedByTokenList(sellToken) ||
    Boolean(
      quote.error instanceof JupiterTokenNotTradableError &&
        sellToken &&
        quote.error.message.includes(sellToken.address),
    );
  const { data: geoData } = useGeoCheck();
  const rwaBlockedTokenAddresses = useMemo(() => {
    const serverBlockedAddresses = currentPrice?.issues?.rwaTokens?.filter(isChainAddress) ?? [];
    return serverBlockedAddresses.length > 0
      ? serverBlockedAddresses
      : [buyToken, sellToken]
          .filter((token) => token && geoData && isTokenRestrictedInCountry(token, geoData.country))
          .map((token) => token!.address);
  }, [buyToken, currentPrice?.issues?.rwaTokens, geoData, sellToken]);
  const isWrapOrUnwrap = Boolean(
    sellToken?.address && buyToken?.address && isWrap(sellToken.address, buyToken.address, chainId),
  );
  const allowanceSpender = currentPrice?.issues?.allowance?.spender;
  const allowanceTarget =
    allowanceSpender && isEthereumAddress(allowanceSpender) ? allowanceSpender : undefined;
  const sellTokenContract =
    sellToken && isEthereumAddress(sellToken.address) ? sellToken.address : undefined;
  const { data: allowance } = useAllowance(sellTokenContract, allowanceTarget, chainId);
  const isSmartWallet = useIsSmartWallet({ chainId });
  const requiresApproval = getIsApprovalRequired({
    isWrap: isWrapOrUnwrap,
    isSolana: chainId === CHAIN_IDS.SOLANA,
    isSmartWallet,
    isCurrentSwapGasless,
    allowance,
    currentPrice,
  });
  const networkFeeNative = useMemo(() => {
    if (!currentPrice || !('totalNetworkFee' in currentPrice)) return 0;
    const totalNetworkFee = currentPrice.totalNetworkFee ?? 0;
    return chainId === CHAIN_IDS.SOLANA
      ? new Decimal(lamportToSol(totalNetworkFee)).toNumber()
      : new Decimal(formatUnits(BigInt(totalNetworkFee), 18)).toNumber();
  }, [chainId, currentPrice]);
  const networkFeeUsd = useMemo(() => {
    if (!currentPrice) return undefined;
    if ('totalNetworkFee' in currentPrice) {
      return new Decimal(networkFeeNative).mul(tokenUsdPrices.nativeTokenUsd).toNumber();
    }
    if (!sellToken || !buyToken) return undefined;
    return calculateZeroExFeeInUsd(
      currentPrice.fees?.zeroExFee,
      sellToken,
      buyToken,
      tokenUsdPrices,
    );
  }, [buyToken, currentPrice, networkFeeNative, sellToken, tokenUsdPrices]);
  const { data: nativeTokenBalanceEvm } = useBalance({
    chainId,
    address: accountAddress && isEthereumAddress(accountAddress) ? accountAddress : undefined,
  });
  const hasInsufficientEvmFeeBalance = useMemo(() => {
    if (
      isCurrentSwapGasless ||
      !nativeTokenBalanceEvm ||
      !networkFeeNative ||
      !sellAmount ||
      !sellTokenContract ||
      sellToken?.decimals === undefined
    ) {
      return false;
    }
    return !getHasEnoughBalanceForAmountPlusFees({
      chainId,
      nativeTokenBalance: formatUnits(nativeTokenBalanceEvm.value, nativeTokenBalanceEvm.decimals),
      networkFeesInEthers: networkFeeNative,
      sellAmount,
      sellTokenAddress: sellTokenContract,
      sellTokenDecimals: sellToken.decimals,
    });
  }, [
    chainId,
    isCurrentSwapGasless,
    nativeTokenBalanceEvm,
    networkFeeNative,
    sellAmount,
    sellToken?.decimals,
    sellTokenContract,
  ]);
  const hasInsufficientFeeBalance = isInsufficientSolForGas || hasInsufficientEvmFeeBalance;
  const validationInput = useMemo(
    () => ({
      mode: 'market' as const,
      sellTokenAddress: sellToken?.address,
      buyTokenAddress: buyToken?.address,
      sellTokenChainId: sellToken?.chainId,
      buyTokenChainId: buyToken?.chainId,
      isSellTokenBlocked,
      isBuyTokenBlocked,
      rwaBlockedTokenAddresses,
      checksApprovalBeforeBalance: false,
      priceImpactPercent: quote.priceImpact?.percent,
      quoteError: quote.quoteError,
      quoteReportsInsufficientBalance: Boolean(currentPrice?.issues?.balance),
      requiresApproval,
      hasInsufficientFeeBalance,
      sellAmountUnits: sellAmount ? BigInt(sellAmount) : undefined,
      sellTokenBalanceUnits: sellTokenBalance?.value,
      networkFeeUsd,
      sellAmountUsd: sellInput.usd,
    }),
    [
      buyToken,
      currentPrice?.issues?.balance,
      hasInsufficientFeeBalance,
      isBuyTokenBlocked,
      isSellTokenBlocked,
      networkFeeUsd,
      quote.priceImpact?.percent,
      quote.quoteError,
      requiresApproval,
      rwaBlockedTokenAddresses,
      sellAmount,
      sellInput.usd,
      sellToken,
      sellTokenBalance?.value,
    ],
  );
  const validationResults = useValidation(validationInput);

  const { isAvailable: isIntentsAvailable } = useIntentsAvailability({
    buyToken,
    chainId,
    sellToken,
  });
  useEffect(() => {
    const shouldShowBanner =
      validationResults.some((result) => result.code === 'insufficient-fee-balance') &&
      !isMatchaAutoOn &&
      isIntentsAvailable &&
      autoBannerDismissedUntil < Date.now() &&
      sellToken?.chainId === connectedChain?.id;
    if (shouldShowBanner) setAutoBannerVisible(true);
  }, [
    autoBannerDismissedUntil,
    connectedChain?.id,
    isIntentsAvailable,
    isMatchaAutoOn,
    sellToken?.chainId,
    setAutoBannerVisible,
    validationResults,
  ]);

  const priceImpact = quote.priceImpact;
  const priceImpactAmount =
    priceImpact && Number.isFinite(priceImpact.percent) && priceImpact.percent < 10 ** 20
      ? `(${commify(priceImpact.percent.toFixed(2))}%)`
      : '';
  const priceImpactStyles =
    priceImpact && Math.abs(priceImpact.percent) > PRICE_IMPACT_THRESHOLDS[0]
      ? priceImpact.percent >= 0
        ? 'text-token-green-1000'
        : 'text-token-red-900'
      : 'text-token-gray-700';
  const isSelectedTokensSameChain = sellToken?.chainId === buyToken?.chainId;
  const shouldShowSwapDetails = Boolean(
    chainId &&
      sellToken &&
      buyToken &&
      hasRequiredPriceParams(sellToken.address, buyToken.address, sellAmount) &&
      isSelectedTokensSameChain &&
      !quote.error,
  );
  const shouldShowSankeyDiagram = isSankeyRouteAvailable(currentPrice, chainId);
  const shouldShowOwnedSellSuggestions = canSuggestOwnedTokens && ownedSuggestionTokens.length > 0;
  const shouldShowExchangeRate = Boolean(
    !isNullAmount(Number(buyInput.decimal)) &&
      !isNullAmount(Number(sellInput.decimal)) &&
      buyToken?.symbol &&
      sellToken?.symbol,
  );
  const noLiquidity = quote.error instanceof ApiNoLiquidityAvailableError;
  const autoSlippageActive = isAutoSlippageActive({ slippageMode });
  const lowSlippage = Boolean(
    !autoSlippageActive && slippage != null && slippage < SLIPPAGE_THRESHOLDS[0],
  );
  const highSlippage = Boolean(
    !autoSlippageActive && slippage != null && slippage > SLIPPAGE_THRESHOLDS[1],
  );

  const handleSwitchTokenButtonClick = () => {
    ampli.tokenPairFlipped({
      buy_token_symbol: buyToken?.symbol,
      sell_token_symbol: sellToken?.symbol,
    });
    flipSelectedBuySellTokens();
  };

  return (
    <>
      <Divider />
      <div className="flex flex-col justify-center py-4 px-6">
        <div className="flex justify-between mb-3 min-h-[18px]">
          <div className={fontStyles({ variant: 'LABEL_SM', class: ['flex items-center'] })}>
            <div>From</div>
            {sellToken ? (
              <>
                <ChainAvatar
                  chainId={sellToken.chainId}
                  alt={`${NETWORK_NAME_PER_CHAIN_ID[sellToken.chainId]} chain logo`}
                  size={12}
                  className="mr-1 ml-1.5"
                />
                <div className="text-token-gray-700">
                  {NETWORK_NAME_PER_CHAIN_ID[sellToken.chainId]}
                </div>
              </>
            ) : null}
          </div>
          {sellToken && accountAddress ? (
            <TokenBalanceLine
              accountAddress={accountAddress}
              chainId={sellToken.chainId}
              tokenAddress={sellToken.address}
            />
          ) : null}
        </div>
        <div className="flex justify-between">
          <div className="flex items-center">
            <TokenSelect
              chainId={chainId}
              onSelect={handleSelectSellToken}
              activeToken={sellToken}
              searchType={SEARCH_TYPES.SELL_TOKEN}
              isLoading={isSellTokenLoading}
            />
            <TokenInfoButton direction="sell" />
          </div>
          {shouldShowOwnedSellSuggestions && buyToken ? (
            <div className="flex gap-1.5 items-center">
              {sellInput.decimal !== '' ? (
                <SellTokenQuickActions
                  orderType="market"
                  shouldShowClear
                  shouldShowActions={false}
                  isTokenBalanceZero={isSellTokenBalanceZero}
                  isDisabled={isLoadingPrice}
                  handleBalancePercentageClick={handleBalancePercentageClick}
                />
              ) : null}
              <InlineSuggestedTokens
                chainId={chainId}
                counterpartTokenAddress={buyToken.address}
                orderType="market"
                ownedTokens={ownedSuggestionTokens}
                onSelect={handleSelectSellToken}
              />
            </div>
          ) : (
            <SellTokenQuickActions
              orderType="market"
              shouldShowClear={Boolean(sellToken?.address && sellInput.decimal !== '')}
              shouldShowActions={Boolean(sellToken)}
              isTokenBalanceZero={isSellTokenBalanceZero}
              isDisabled={isLoadingPrice || !buyToken?.address || isInsufficientSolForGas}
              loadingFraction={
                estimateNativeFeeReservePrice.isPending
                  ? estimateNativeFeeReservePrice.variables?.fraction
                  : undefined
              }
              handleBalancePercentageClick={handleBalancePercentageClick}
            />
          )}
        </div>
        <AmountInputGroup
          buyInput={buyInput}
          direction="sell"
          decimals={sellToken?.decimals}
          sellInput={sellInput}
          symbol={sellToken?.symbol}
          tokenUsdPrices={tokenUsdPrices}
          updateBuyInput={updateBuyInput}
          updateSellInput={updateSellInput}
          updateInputs={updateInputs}
          setInputMode={setInputMode}
        />
      </div>
      <div className="flex items-center">
        <Divider />
        <SwitchTokensButton
          isLoading={isLoadingPrice}
          direction="normal"
          onClick={handleSwitchTokenButtonClick}
        />
        <Divider />
      </div>
      <div className="flex flex-col justify-center py-4 px-6">
        <div className="flex justify-between mb-3 min-h-[18px]">
          <div className={fontStyles({ variant: 'LABEL_SM', class: ['flex items-center'] })}>
            <div>To</div>
            {buyToken ? (
              <>
                <ChainAvatar
                  chainId={buyToken.chainId}
                  alt={`${NETWORK_NAME_PER_CHAIN_ID[buyToken.chainId]} chain logo`}
                  size={12}
                  className="mr-1 ml-1.5"
                />
                <div className="text-token-gray-700">
                  {NETWORK_NAME_PER_CHAIN_ID[buyToken.chainId]}
                </div>
              </>
            ) : null}
          </div>
          {shouldShowExchangeRate && buyToken && sellToken ? (
            <ExchangeRate
              buyTokenSymbol={buyToken.symbol}
              sellTokenSymbol={sellToken.symbol}
              exchangeRate={exchangeRate}
              isLoading={isLoadingPrice}
            />
          ) : buyToken?.address && accountAddress ? (
            <TokenBalanceLine
              accountAddress={accountAddress}
              chainId={buyToken.chainId}
              tokenAddress={buyToken.address}
            />
          ) : null}
        </div>
        <div className="flex justify-between">
          <div className="flex items-center">
            <TokenSelect
              chainId={chainId}
              onSelect={handleSelectBuyToken}
              activeToken={buyToken}
              searchType={SEARCH_TYPES.BUY_TOKEN}
              isLoading={isBuyTokenLoading}
            />
            <TokenInfoButton direction="buy" />
          </div>
          {!buyToken && sellToken ? (
            <InlineSuggestedTokens
              accountAddress={accountAddress}
              chainId={chainId}
              counterpartTokenAddress={sellToken.address}
              orderType="market"
              onSelect={handleSelectBuyToken}
            />
          ) : null}
        </div>
        <AmountInputGroup
          buyInput={buyInput}
          direction="buy"
          disabled
          shimmerOnUpdate
          decimals={buyToken?.decimals}
          sellInput={sellInput}
          symbol={buyToken?.symbol}
          tokenUsdPrices={tokenUsdPrices}
          updateBuyInput={updateBuyInput}
          updateSellInput={updateSellInput}
          updateInputs={updateInputs}
          setInputMode={setInputMode}
        >
          <div
            data-disabled={isLoadingPrice}
            className={fontStyles({
              variant: 'BODY_MD',
              class:
                'mb-1.5 whitespace-nowrap text-token-gray-700 block data-[disabled=true]:opacity-50',
            })}
          >
            <div className="mb-1.5 whitespace-nowrap inline-block px-1">
              {buyInput.usd ? formatFiat(buyInput.usd) : buyToken ? formatFiat(0) : ''}
            </div>
            {buyToken?.address &&
            sellToken?.address &&
            buyInput.decimal !== '' &&
            priceImpact &&
            !Number.isNaN(priceImpact.percent) ? (
              <MatchaTooltip>
                <MatchaTooltipTrigger>
                  <span className={priceImpactStyles}>
                    &nbsp;
                    {priceImpactAmount}
                  </span>
                </MatchaTooltipTrigger>
                <MatchaTooltipContent>
                  <TooltipPriceImpact />
                </MatchaTooltipContent>
                <MatchaTooltipPopover sideOffset={16} />
                <MatchaTooltipBottomSheet header="Price impact" />
              </MatchaTooltip>
            ) : null}
          </div>
        </AmountInputGroup>
      </div>
      {shouldShowSwapDetails && sellToken && buyToken ? (
        <>
          <Divider />
          <div className="flex flex-col justify-center">
            <ReceiveRecapLineMarketMode
              isLoading={isLoadingPrice}
              buyAmount={currentPrice?.buyAmount}
              sellAmount={currentPrice?.sellAmount}
              buyToken={buyToken}
              sellToken={sellToken}
              fees={currentPrice?.fees}
              isGasless={hasInTokenGasFee(currentPrice)}
              isStablecoinPair={isStablecoinPair}
            />
          </div>
          <Divider />
          <div className="flex flex-col justify-center">
            <SectionErrorBoundary fallbackLabel="Swap details unavailable" resetKey={currentPrice}>
              <SwapDetailsAccordionMarketMode
                chainId={chainId}
                currentPrice={currentPrice}
                isWrap={isWrapOrUnwrap}
                isLoading={isLoadingPrice}
                sellToken={sellToken}
                buyToken={buyToken}
                isCurrentSwapGasless={isCurrentSwapGasless}
                walletAddress={accountAddress}
                connectedWallet={ethereumWallet}
                nativeTokenUsd={tokenUsdPrices.nativeTokenUsd}
                tokenUsdPrices={tokenUsdPrices}
              />
            </SectionErrorBoundary>
          </div>
        </>
      ) : null}
      <Divider />
      <div
        className={classNames('px-6 mt-6 z-10 mb-0 bottom-4 w-full', {
          'fixed tablet:static': variant === 'fullpage',
        })}
      >
        <TradeCta
          validationResults={validationResults}
          quote={quote}
          allowanceTarget={allowanceTarget}
        />
      </div>
      <div className="px-6 mt-6">
        {currentPrice && (highSlippage || lowSlippage) && typeof slippage === 'number' ? (
          <SlippageAlert
            slippageType={highSlippage ? 'high' : 'low'}
            percentage={slippage * 100}
            showMessage
            setShowTradeSettings={setSettingsOpen}
          />
        ) : null}
      </div>
      <CheckoutAlerts results={validationResults} quote={quote} />
      <InlineError error={quote.error} noLiquidity={noLiquidity} />
      <SuperstateKYCAlert buyToken={buyToken} sellToken={sellToken} />
      {autoBannerVisible && isMobile ? (
        <MatchaAutoBanner chainId={chainId} isMobile setAutoBannerVisible={setAutoBannerVisible} />
      ) : null}
      {shouldShowSankeyDiagram && currentPrice ? (
        <SankeyModal
          chainId={chainId}
          route={currentPrice.route}
          isOpen={sankeyModalOpen}
          onClose={() => setSankeyModalOpen(false)}
        />
      ) : null}
    </>
  );
}
